import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, Eye, Sparkles, ArrowRight, Flame, Loader2, Mail, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_META = {
  hay: { nl: "Hooi-rantsoen", en: "Hay Ration", emoji: "🌾", accent: "amber", path_nl: "/hooi-rantsoen-calculator", path_en: "/hay-ration-calculator" },
  cost: { nl: "Paardenkosten", en: "Horse Cost", emoji: "💰", accent: "emerald", path_nl: "/paardenkosten-calculator", path_en: "/horse-cost-calculator" },
  farrier: { nl: "Hoefsmid", en: "Farrier", emoji: "🦄", accent: "stone", path_nl: "/hoefsmid-planner", path_en: "/farrier-schedule-calculator" },
  foaling: { nl: "Veulendatum", en: "Foaling", emoji: "🐴", accent: "pink", path_nl: "/veulendatum-calculator", path_en: "/foaling-date-calculator" },
  trailer: { nl: "Trailer", en: "Trailer", emoji: "🚛", accent: "blue", path_nl: "/trailer-gewicht-calculator", path_en: "/trailer-weight-calculator" },
};

const ACCENT_PILL = {
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  stone: "bg-stone-200 text-stone-800 border-stone-300",
  pink: "bg-pink-100 text-pink-800 border-pink-200",
  blue: "bg-blue-100 text-blue-800 border-blue-200",
};

const formatSummary = (tool, inputs, isNl) => {
  if (!inputs) return "—";
  if (tool === "hay") return `${inputs.weight || "?"} kg · ${inputs.hay_kg || "?"} kg ${isNl ? "hooi" : "hay"}/dag`;
  if (tool === "cost") return `€${inputs.monthly || "?"}/${isNl ? "mnd" : "mo"} · €${inputs.yearly || "?"}/${isNl ? "jr" : "yr"}`;
  if (tool === "farrier") return `${inputs.weeks || "?"} ${isNl ? "wkn" : "wks"} · ${inputs.discipline || "?"}`;
  if (tool === "foaling") return `${inputs.expected_date || "?"} · ${inputs.gestation || "?"}d`;
  if (tool === "trailer") return `${inputs.license || "?"} · ${inputs.combo || "?"} kg · ${inputs.horses || 0}🐎`;
  return "—";
};

const timeAgo = (iso, isNl) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return isNl ? `${Math.max(1, Math.floor(diff / 60))}m geleden` : `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return isNl ? `${Math.floor(diff / 3600)}u geleden` : `${Math.floor(diff / 3600)}h ago`;
  return isNl ? `${Math.floor(diff / 86400)}d geleden` : `${Math.floor(diff / 86400)}d ago`;
};

const TopSharedPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [tool, setTool] = useState("all");
  const [state, setState] = useState({ loading: true, items: [], totals: {} });

  useEffect(() => {
    document.title = isNl
      ? "🔥 Trending paarden-analyses — meest gedeelde berekeningen | HorseAppCompare"
      : "🔥 Trending horse analyses — most-shared calculations | HorseAppCompare";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", isNl
      ? "Ontdek de populairste paarden-berekeningen wereldwijd: voer, kosten, hoefsmid en meer — gedeeld door duizenden eigenaren."
      : "Discover the most popular horse calculations worldwide: feed, costs, farrier and more — shared by thousands of owners.");
  }, [isNl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        const params = tool === "all" ? {} : { tool };
        const { data } = await axios.get(`${API}/top-shared`, { params });
        if (!cancelled) setState({ loading: false, items: data.items || [], totals: data.totals || {} });
      } catch (e) {
        if (!cancelled) setState({ loading: false, items: [], totals: {} });
      }
    })();
    return () => { cancelled = true; };
  }, [tool]);

  const totalViews = useMemo(() => {
    return Object.values(state.totals).reduce((s, t) => s + (t.total_views || 0), 0);
  }, [state.totals]);

  const [sub, setSub] = useState({ email: "", status: "idle", message: "" });
  const submitSub = async (e) => {
    e.preventDefault();
    if (!sub.email || !sub.email.includes("@")) return;
    setSub({ ...sub, status: "loading", message: "" });
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, { email: sub.email.trim().toLowerCase(), language: lang, source: "trending" });
      const msgs = {
        already_subscribed: isNl ? "Je was al ingeschreven ✓" : "You're already subscribed ✓",
        pending_confirmation: isNl ? "Check je inbox — bevestig de inschrijving!" : "Check your inbox — confirm the subscription!",
        pending_email_failed: isNl ? "We konden de bevestigingsmail niet sturen, je staat in de wachtlijst." : "We couldn't send the confirmation email — you're on the wait list.",
        pending_no_smtp: isNl ? "Je bent geregistreerd — eerste mail volgt zodra de verzending is opgezet." : "You're registered — first email arrives once sending is set up.",
      };
      setSub({ email: "", status: "success", message: msgs[data.status] || (isNl ? "Bedankt!" : "Thanks!") });
    } catch (err) {
      setSub({ ...sub, status: "error", message: err?.response?.data?.detail || (isNl ? "Er ging iets mis." : "Something went wrong.") });
    }
  };

  return (
    <main data-testid="top-shared-page">
      <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-white border-b border-[#E2E8F0]">
        <div className="hac-container py-14 max-w-4xl">
          <div className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-800 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            <Flame size={12} /> {isNl ? "Trending" : "Trending"}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-3">{isNl ? "🔥 Meest gedeelde paarden-analyses" : "🔥 Most-shared horse analyses"}</h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">{isNl
            ? "De populairste berekeningen van paarden­eigenaren wereldwijd. Klik op een analyse om te zien wat onze AI adviseert."
            : "The most popular calculations from horse owners worldwide. Click any analysis to see what our AI recommends."}</p>
          {totalViews > 0 && (
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#64748B] bg-white border border-[#E2E8F0] rounded-full px-4 py-2">
              <Eye size={14} className="text-orange-500" />
              <span><strong className="text-[#0F172A]">{totalViews.toLocaleString(isNl ? "nl-NL" : "en-GB")}</strong> {isNl ? "totale weergaven" : "total views"}</span>
            </div>
          )}
        </div>
      </section>

      <section className="hac-container py-8 max-w-6xl">
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setTool("all")} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tool === "all" ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#0F172A]"}`} data-testid="top-filter-all">
            {isNl ? "Alle" : "All"} {totalViews > 0 && <span className="opacity-70">({Object.values(state.totals).reduce((s, t) => s + (t.count || 0), 0)})</span>}
          </button>
          {Object.entries(TOOL_META).map(([key, meta]) => {
            const t = state.totals[key];
            const active = tool === key;
            return (
              <button key={key} onClick={() => setTool(key)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${active ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#0F172A]"}`} data-testid={`top-filter-${key}`}>
                <span>{meta.emoji}</span> {isNl ? meta.nl : meta.en} {t && <span className="opacity-70">({t.count})</span>}
              </button>
            );
          })}
        </div>

        {state.loading ? (
          <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-[#2563EB]" /></div>
        ) : state.items.length === 0 ? (
          <div className="py-16 text-center text-[#64748B]">
            <Sparkles size={32} className="mx-auto mb-3 opacity-50" />
            <p className="mb-1">{isNl ? "Nog geen trending analyses voor deze categorie." : "No trending analyses for this category yet."}</p>
            <p className="text-sm">{isNl ? "Wees de eerste — genereer een analyse en deel hem." : "Be the first — generate an analysis and share it."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {state.items.map((item, idx) => {
              const meta = TOOL_META[item.tool];
              if (!meta) return null;
              const path = item.language === "nl" ? meta.path_nl : meta.path_en;
              return (
                <Link key={item.share_token} to={`/share/${item.share_token}`} className="group bg-white border border-[#E2E8F0] hover:border-[#0F172A] hover:shadow-lg transition-all rounded-2xl p-5 flex flex-col" data-testid={`top-item-${idx}`}>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${ACCENT_PILL[meta.accent]}`}>
                      <span>{meta.emoji}</span> {isNl ? meta.nl : meta.en}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-[#94A3B8]">
                      <Eye size={12} /> {item.views}
                    </span>
                  </div>
                  <div className="text-sm font-bold text-[#0F172A] mb-1 font-mono">{formatSummary(item.tool, item.inputs, isNl)}</div>
                  <p className="text-xs text-[#64748B] leading-relaxed flex-1 line-clamp-3">{item.preview}…</p>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F1F5F9]">
                    <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{timeAgo(item.created_at, isNl)} · {(item.language || "en").toUpperCase()}</span>
                    <span className="text-xs text-[#2563EB] inline-flex items-center gap-1 group-hover:gap-1.5 transition-all"><Sparkles size={11} /> {isNl ? "Bekijk" : "View"} <ArrowRight size={11} /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-12 bg-[#0F172A] rounded-2xl p-8 text-white text-center">
          <TrendingUp size={32} className="mx-auto mb-3 opacity-80" />
          <h2 className="font-heading text-2xl md:text-3xl mb-2">{isNl ? "Wil je in deze lijst staan?" : "Want to be on this list?"}</h2>
          <p className="text-white/70 mb-5 text-sm max-w-md mx-auto">{isNl ? "Gebruik een van onze gratis tools, genereer je AI-analyse en deel hem met je netwerk." : "Use one of our free tools, generate your AI analysis and share it with your network."}</p>
          <Link to={isNl ? "/paardenkosten-calculator" : "/horse-cost-calculator"} className="bg-emerald-600 hover:bg-emerald-700 text-white px-7 py-3 rounded-full inline-flex items-center gap-2 transition-colors font-medium">
            {isNl ? "Start een berekening" : "Start a calculation"} <ArrowRight size={16} />
          </Link>
        </div>

        <div className="mt-6 bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 rounded-2xl p-8 text-white">
          <div className="max-w-md mx-auto text-center">
            <Mail size={32} className="mx-auto mb-3 opacity-90" />
            <h2 className="font-heading text-2xl md:text-3xl mb-2">{isNl ? "📬 Wekelijkse Trending Digest" : "📬 Weekly Trending Digest"}</h2>
            <p className="text-white/90 mb-5 text-sm">{isNl ? "Elke maandag de 5 populairste paardenanalyses in je inbox. Gratis, één klik om uit te schrijven." : "Every Monday the 5 most popular horse analyses in your inbox. Free, one click to unsubscribe."}</p>
            {sub.status === "success" ? (
              <div className="bg-white/15 backdrop-blur rounded-xl p-4 text-sm inline-flex items-center gap-2" data-testid="newsletter-success">
                <Check size={16} /> {sub.message}
              </div>
            ) : (
              <form onSubmit={submitSub} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto" data-testid="newsletter-form">
                <input
                  type="email" required
                  value={sub.email}
                  onChange={(e) => setSub({ ...sub, email: e.target.value })}
                  placeholder={isNl ? "jouw@email.com" : "you@email.com"}
                  className="flex-1 px-4 py-3 rounded-full text-[#0F172A] text-sm placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-white"
                  data-testid="newsletter-input"
                />
                <button type="submit" disabled={sub.status === "loading"} className="bg-[#0F172A] hover:bg-[#1E293B] disabled:opacity-60 text-white px-6 py-3 rounded-full text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors" data-testid="newsletter-submit">
                  {sub.status === "loading" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  {isNl ? "Schrijf me in" : "Sign me up"}
                </button>
              </form>
            )}
            {sub.status === "error" && <p className="text-xs text-white/80 mt-2" data-testid="newsletter-error">{sub.message}</p>}
            <p className="text-[10px] text-white/70 mt-3">{isNl ? "Geen spam. Geen verkoop van data. Uitschrijven met 1 klik." : "No spam. No data selling. Unsubscribe with 1 click."}</p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TopSharedPage;
